/**
 * Login rapide : ressaisie du code d'accès (agent déjà enregistré).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, AlertCircle, Loader2, UserCircle, LogOut } from 'lucide-react';
import { connexion, getAgent, clearSession } from '../services/sessionApi';

export default function Login() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const agent = getAgent();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (code.trim().length < 3) {
      setError("Code d'accès requis.");
      return;
    }
    setLoading(true);
    try {
      await connexion(code.trim());
      navigate('/verif', { replace: true });
    } catch (err: any) {
      if (err.code === 'onboarding_requis') {
        // Appareil inconnu → onboarding
        navigate('/verif/onboarding', { replace: true });
      } else if (err.code === 'code_invalide') {
        setError("Code d'accès incorrect.");
      } else if (err.status === 403) {
        setError(err.message || 'Accès révoqué. Contactez la caisse.');
      } else if (err.status === 429) {
        setError('Trop de tentatives. Patientez quelques minutes.');
      } else {
        setError(err.message || 'Erreur, réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  const changerAgent = () => {
    clearSession();
    navigate('/verif/onboarding', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="inline-block bg-white rounded-2xl p-3 shadow-xl mb-3">
            <img src="/verif-icon-192.png" alt="2i Recouvrement"
                 className="w-16 h-16 rounded-lg" />
          </div>
          <div className="text-white text-2xl font-bold">2i Recouvrement</div>
          {agent && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-white text-sm">
              <UserCircle size={16} /> {agent.nom_complet}
            </div>
          )}
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl shadow-2xl p-5 space-y-4"
        >
          <div className="flex items-center gap-2 text-blue-900 font-semibold">
            <KeyRound size={18} />
            <span>Code d'accès</span>
          </div>

          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full px-3 py-4 border-2 border-gray-300 rounded-lg text-2xl tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••"
            inputMode="numeric"
            autoComplete="current-password"
            autoFocus
            required
          />

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Connexion…</>
                     : 'Se connecter'}
          </button>

          {agent && (
            <button
              type="button"
              onClick={changerAgent}
              className="w-full text-xs text-gray-500 hover:text-gray-700 py-1 flex items-center justify-center gap-1"
            >
              <LogOut size={11} /> Changer d'agent
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
